import { Component, OnInit, computed, input, model, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { FormValueControl } from "@angular/forms/signals"
import { SearchableSelectOption } from "src/app/models"
import { CategoryService } from "src/app/services"
import { SearchableSelectComponent } from "../searchable-select/searchable-select.component"
import { MatIcon } from "@angular/material/icon"

interface Option {
    masterId: string
    name: string
}

@Component({
    selector: "valben-categories-input",
    templateUrl: "./categories-input.component.html",
    standalone: true,
    imports: [NgClass, SearchableSelectComponent, MatIcon],
})
export class CategoriesInputComponent implements FormValueControl<string[]>, OnInit {
    value = model.required<string[]>()
    type = input.required<string>()
    title = input("")
    error = input(false)
    invalid = input(false)

    hasError = computed(() => this.error() || this.invalid())

    /** Chips to render: the picked ids resolved to names. */
    selected = computed<Option[]>(() => {
        const names = this._namesById()
        return this.value().map(id => ({ masterId: id, name: names.get(id) ?? "" }))
    })

    dropdownOpen = false
    pickedId: string | null = null

    options = signal<SearchableSelectOption<string>[]>([])
    availableOptions = computed(() => this.options().filter(option => !this.value().includes(option.value)))
    loading = signal(false)
    private _namesById = signal(new Map<string, string>())
    private _search = ""
    private _total = 0
    private readonly _take = 10

    constructor(private _categoryService: CategoryService) {}

    ngOnInit(): void {
        this._categoryService.GetAllCached().subscribe(categories => {
            const wanted = this.type().toLowerCase()
            this._learnNames(
                categories
                    .filter(c => c.type?.toLowerCase() === wanted)
                    .map(c => ({ masterId: c.masterId, name: c.names?.["hr"] ?? "" }))
            )
        })
    }

    add(id: string | null): void {
        this.pickedId = null
        if (!id) return

        if (this.value().includes(id)) return
        this.value.update(ids => [...ids, id])
    }

    remove(index: number): void {
        this.value.update(ids => ids.filter((_, i) => i !== index))
    }

    toggleDropdown(): void {
        this.dropdownOpen = !this.dropdownOpen
        this.pickedId = null
        this._search = ""
        this.options.set([])
        this._total = 0
    }

    /** Debounced by the select; also fires once with "" when the panel opens, which loads the first window. */
    onSearch(query: string): void {
        this._search = query
        this._loadWindow(0, true)
    }

    onScrolledToEnd(): void {
        if (this.options().length < this._total) this._loadWindow(this.options().length, false)
    }

    private _loadWindow(skip: number, replace: boolean): void {
        const search = this._search
        this.loading.set(true)
        this._categoryService.GetLookup(this.type(), search, skip, this._take, true).subscribe({
            next: res => {
                // Drop a slow response whose search no longer matches, so it can't clobber a newer one.
                if (search !== this._search) {
                    this.loading.set(false)
                    return
                }
                this._total = res.totalCount
                const window = res.items.map(c => ({ masterId: c.masterId, name: c.names?.["hr"] ?? "" }))
                this.options.update(current => [
                    ...(replace ? [] : current),
                    ...window.map(o => ({ value: o.masterId, label: o.name, searchText: o.name })),
                ])
                this._learnNames(window)
                this.loading.set(false)
            },
            error: () => this.loading.set(false),
        })
    }

    private _learnNames(options: Option[]): void {
        this._namesById.update(map => {
            const next = new Map(map)
            options.forEach(o => next.set(o.masterId, o.name))
            return next
        })
    }
}
